<template>
  <div
    id="connectorView"
    class="page"
  >
    <div class="bg-white shadow-sm p-3">
      <div class="d-flex justify-content-between">
        <div class="d-flex align-items-center">
          <b-button
            id="goBack"
            variant="light"
            class="mr-3"
            @click="$router.push({name: 'connectorScreen'})"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
            Back
          </b-button>
          <h6 class="m-0">
            {{ connector.vendor }}
          </h6>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            id="generateAll"
            variant="primary"
            size="sm"
            @click="onSaveAllClick"
          >
            Generate All
          </b-button>
        </div>
      </div>
    </div>
    <content-spinner :show="loading || viewLoading" />
    <div
      v-if="!isSQLServer"
      class="connector-content"
    >
      <div
        v-for="card in cards"
        v-if="!loading && !viewLoading"
        class="pt-3 pl-3 pr-3"
        :class="{'pb-3': card.isLast}"
      >
        <b-card
          no-body
          class="connector-card"
          :class="{'static-endpoints-card': isStaticEndpointsCard(card)}"
        >
          <template slot="header">
            <div class="d-flex justify-content-between">
              <div
                class="header-toggle align-items-center"
                @click="onCollapseClick(card)"
              >
                <font-awesome-icon
                  v-if="card.collapsed"
                  :icon="['fas',card.collapsed ? 'chevron-down' : 'chevron-up']"
                  class="mr-2"
                />
                <b>{{ card.name }}</b>
              </div>
              <div
                v-if="isStaticEndpointsCard(card)"
                class="search-input"
              >
                <b-form>
                  <b-form-group>
                    <b-input-group>
                      <input-widget
                        id="searchInput"
                        :field="searchInput"
                        :model="filter"
                        @input="search"
                      />
                      <b-button
                        v-if="filter.keyword"
                        id="clearSearchBtn"
                        variant="transparent"
                        class="transparent search-clear-btn"
                        style="margin-left: -40px; z-index: 100;"
                        @click="resetSearchData"
                      >
                        <font-awesome-icon :icon="['fas', 'times']" />
                      </b-button>
                    </b-input-group>
                  </b-form-group>
                </b-form>
              </div>
              <b-button
                v-if="(!isDealCloud && !isDynamo && !isSalesforce) && (isEndpointsCard(card.key) || isStartingPointsCard(card.key))"
                :id="isEndpointsCard(card.key) ? 'addEndpoint' : 'addStartingPoint'"
                variant="secondary"
                size="sm"
                @click="onAddClick(isEndpointsCard(card.key))"
              >
                <font-awesome-icon :icon="['fas', 'plus']" />
                Add {{ isEndpointsCard(card.key) ? 'Endpoint' : 'Starting Point' }}
              </b-button>
            </div>
          </template>
          <b-collapse
            :id="card.key"
            :visible="!card.collapsed"
          >
            <b-form
              v-if="card.key === 'authDetails'"
              class="p-3"
            >
              <div class="row">
                <div
                  v-for="field in fields"
                  class="col-md-6"
                >
                  <input-widget
                    :id="`auth-${field.name}-field`"
                    :model="form"
                    :field="field"
                  />
                </div>
              </div>
              <b-button
                id="testAuth"
                variant="primary"
                class="float-right mb-3"
              >
                Test
              </b-button>
            </b-form>
            <b-card
              v-if="isEndpointsCard(card.key) && connector.endpoints"
              no-body
            >
              <b-card-body
                :class="{'overflow-unset': isDealCloud || isSalesforce}"
                class="endpoint-content p-0"
              >
                <no-content
                  :show="!connector.endpoints.length && !endpointLoading"
                  :text="`Endpoints not exist`"
                />
                <content-spinner :show="endpointLoading" />
                <connector-points-table
                  v-if="!endpointLoading"
                  :key="updateContent"
                  :items="connector.endpoints"
                  :is-endpoint="true"
                  :action-buttons="actionButtons"
                  :options="{isDealCloud, isSalesforce, endpointEntryColumns}"
                  @on-preview-click="onPreviewClick"
                  @on-save-click="onSaveClick"
                  @on-edit-click="onEditClick"
                  @on-update="onUpdate"
                  @update-endpoint="onUpdateEndpoint"
                  @on-delete-click="onDeleteClick"
                />
              </b-card-body>
            </b-card>
            <b-card
              v-if="isStaticEndpointsCard(card)"
              no-body
            >
              <b-card-body class="endpoint-content p-0">
                <no-content
                  :show="!staticEndpointsExist"
                  text="Endpoints not exist"
                />
                <connector-static-points-table
                  v-if="!loading && staticEndpointsExist"
                  :key="updateContent"
                  :items="staticEndpointItems"
                  @on-change-config-status="onStatusChange"
                />
              </b-card-body>
            </b-card>
            <b-card
              v-if="isStartingPointsCard(card.key)"
              no-body
            >
              <b-card-body class="endpoint-content p-0">
                <no-content
                  :show="!Object.keys(connector.startingPoints).length && !startingPointLoading"
                  :text="`Starting Points not exist`"
                />
                <content-spinner :show="startingPointLoading" />
                <connector-points-table
                  v-if="!startingPointLoading"
                  :items="connector.startingPoints"
                  :options="pitchBookEndpoints"
                  @on-edit-click="onEditClick"
                  @on-delete-click="onDeleteClick"
                  @on-save-click="onSaveClick"
                />
              </b-card-body>
            </b-card>
            <b-card
              v-if="card.key === 'preview'"
              no-body
            >
              <b-card-body class="preview-content p-0">
                <no-content
                  :show="!preview.data.length && !preview.loading"
                  :text="`Preview not exist`"
                />
                <content-spinner :show="preview.loading" />
                <b-table
                  v-if="preview.data.length > 0 && !preview.loading"
                  class="connector-table mb-0"
                  :items="preview.data"
                  :head-variant="'dark'"
                  :small="true"
                />
              </b-card-body>
            </b-card>
          </b-collapse>
        </b-card>
      </div>
    </div>
    <EndpointModal />
    <StartingPointModal v-if="isPitchBook" />
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../core/components/ContentSpinner';
import InputWidget from '../../core/components/complex-table/components/InputWidget';
import EndpointModal from './modals/EndpointModal';
import NoContent from '../../core/components/NoContent';
import ConnectorPointsTable from './components/ConnectorPointsTable';
import ConnectorStaticPointsTable from './components/ConnectorStaticPointsTable';
import StartingPointModal from './modals/StartingPointModal';
import _ from 'lodash';
import {v4 as uuidv4} from 'uuid';

const {mapState, mapActions} = createNamespacedHelpers('connectorScreen');
const {mapActions: mapDataManagementActions} = createNamespacedHelpers('dataManagement');
export default {
  name: 'ConnectorView',
  components: {
    StartingPointModal,
    ConnectorPointsTable,
    NoContent,
    EndpointModal,
    InputWidget,
    ContentSpinner,
    ConnectorStaticPointsTable,
  },
  data() {
    return {
      searchInput: {
        type: 'text',
        label: false,
        placeholder: 'Search',
        name: 'keyword',
      },
      filter: {
        keyword: '',
      },
      viewLoading: false,
      connector: {},
      form: {
        username: '',
        password: '',
        accountId: '',
        token: '',
        ClientId: '',
        server: null,
        region: null,
        APIKey: '',
        ClientSecret: null,
        version: null,
        IsSandbox: false,
        AccessKey: null,
        tenant: null,
      },
      connectorCards: [
        {key: 'authDetails', name: 'Auth Details', collapsed: true},
        {key: 'staticEndpoints', name: 'Static Endpoints', collapsed: false},
        {key: 'endpoints', name: 'Endpoints', collapsed: false},
        {key: 'startingPoints', name: 'Starting Points', collapsed: false},
        {key: 'preview', name: 'Preview', collapsed: true, isLast: true},
      ],
      staticEndpointItems: [],
      updateContent: 0,
    };
  },
  watch: {
    connectors() {
      this.connector = {...this.connectors.find(c => c.configurationId === this.$route.params.configurationId)};
    },
  },
  computed: {
    ...mapState(['connectors', 'loading', 'preview']),
    ...mapState({
      endpointLoading: state => state.endpoint.loading,
      startingPointLoading: state => state.startingPoint.loading,
      pitchBookEndpoints: state => state.startingPoint.pitchBookEndpoints,
      endpointEntryColumns: state => state.endpointEntryColumns,
      staticEndpoints: state => state.staticEndpoints.data,
    }),
    actionButtons() {
      return {
        preview: true,
        save: true,
        edit: !this.isDealCloud && !this.isDynamo && !this.isEfront && !this.isSalesforce,
        update: this.isDealCloud || this.isSalesforce,
        delete: !this.isDealCloud && !this.isDynamo && !this.isEfront && !this.isSalesforce,
      };
    },
    fields() {
      const fields = {};
      if (this.isHarvest) {
        this.$nextTick(() => {
          fields.accountId = {
            type: 'text',
            label: 'Account ID',
            name: 'accountId',
          };
          fields.token = {
            type: 'text',
            label: 'Token',
            name: 'token',
          };
        });
      } else if (this.isPitchBook || this.isDynamo) {
        this.$nextTick(() => {
          fields.token = {
            type: 'text',
            label: 'Token',
            name: 'token',
          };
        });
      } else if (this.isDealCloud) {
        this.$nextTick(() => {
          fields.ClientId = {
            type: 'text',
            label: 'Client Id',
            name: 'ClientId',
          };
          fields.APIKey = {
            type: 'password',
            label: 'API Key',
            name: 'APIKey',
          };
        });
      } else if (this.isSnowflake) {
        this.$nextTick(() => {
          fields.server = {
            type: 'text',
            label: 'Server',
            name: 'server',
          };
          fields.region = {
            type: 'text',
            label: 'Region',
            name: 'region',
          };
          fields.username = {
            type: 'text',
            label: 'Username',
            name: 'username',
          };
          fields.password = {
            type: 'password',
            label: 'Password',
            name: 'password',
          };
        });
      } else if (this.isSalesforce) {
        fields.ClientId = {
          type: 'text',
          label: 'ClientId',
          name: 'ClientId',
        };
        fields.ClientSecret = {
          type: 'password',
          label: 'Client Secret',
          name: 'ClientSecret',
        };
        fields.username = {
          type: 'text',
          label: 'Username',
          name: 'username',
        };
        fields.password = {
          type: 'password',
          label: 'Password',
          name: 'password',
        };
        fields.version = {
          type: 'text',
          label: 'Version',
          name: 'version',
        };
        fields.tenant = {
          type: 'text',
          label: 'Tenant',
          name: 'tenant',
        };
        fields.IsSandbox = {
          type: 'checkbox',
          labelPosition: 'top',
          label: 'Is Sandbox',
          name: 'IsSandbox',
        };
        if (this.form.IsSandbox) {
          fields.AccessKey = {
            type: 'text',
            label: 'AccessKey',
            name: 'AccessKey',
          };
        }
      } else if (this.isBlackMountain) {
        this.$nextTick(() => {
          fields.ClientId = {
            type: 'text',
            label: 'Client ID',
            name: 'ClientId',
          };
          fields.ClientSecret = {
            type: 'text',
            label: 'Client Secret',
            name: 'ClientSecret',
          };
          fields.Scope = {
            type: 'text',
            label: 'Scope',
            name: 'Scope',
          };
        });
      } else {
        this.$nextTick(() => {
          fields.username = {
            type: 'text',
            label: 'Username',
            name: 'username',
          };
          fields.password = {
            type: 'password',
            label: 'Password',
            name: 'password',
          };
        });
      }
      return fields;
    },
    isBlackMountain() {
      return ['BlackMountain', '0'].includes(this.connector.vendor);
    },
    isHarvest() {
      return ['Harvest', '1'].includes(this.connector.vendor);
    },
    isPitchBook() {
      return ['PitchBook', '2'].includes(this.connector.vendor);
    },
    isSQLServer() {
      return ['SQLServer', '3'].includes(this.connector.vendor);
    },
    isDealCloud() {
      return ['DealCloud', '8'].includes(this.connector.vendor);
    },
    isDynamo() {
      return ['Dynamo', '7'].includes(this.connector.vendor);
    },
    isSnowflake() {
      return ['Snowflake', '5'].includes(this.connector.vendor);
    },
    isEfront() {
      return ['Efront', '9'].includes(this.connector.vendor);
    },
    isSalesforce() {
      return ['Salesforce', '10'].includes(this.connector.vendor);
    },
    cards() {
      let cards;

      if (this.isPitchBook) {
        cards = ['authDetails', 'startingPoints'];
      } else if (this.isDealCloud || this.isDynamo || this.isEfront || this.isSalesforce) {
        cards = ['authDetails', 'staticEndpoints', 'endpoints', 'preview'];
      } else {
        cards = ['authDetails', 'endpoints', 'preview'];
      }

      return this.connectorCards.filter(card => cards.includes(card.key));
    },
    staticEndpointsExist() {
      return !!Object.keys(this.staticEndpointItems).length;
    },
  },
  methods: {
    ...mapActions(['getAllConnector', 'showEndpointModal', 'deleteEndpoint', 'showEndpointModal', 'getEndpointPreview',
      'clearPreviewData', 'saveEndpointPreview', 'saveAllEndpointPreview', 'showStartingPointModal', 'deleteStartingPoint',
      'getPitchBookEndpoints', 'generatePitchBook', 'generateSQLServer', 'getStaticEndpoints', 'createStaticEndpoint', 'updateEndpointEntity', 'getEndpointEntryColumns', 'saveEndpoint',
      'getEfrontReports',
      'updateStaticEndpoint',
      'clearStaticEndpointsData']),
    ...mapDataManagementActions(['getFields']),
    onCollapseClick(card) {
      card.collapsed = !card.collapsed;
      this.$root.$emit('bv::toggle::collapse', card.key);
    },
    onEditClick(item, isEndpoint) {
      if (isEndpoint) {
        this.showEndpointModal({
          ...item,
          vendor: this.connector.vendor,
        });
      } else {
        this.showStartingPointModal(item);
      }
    },
    async onUpdate(item) {
      const response = await this.updateEndpointEntity(item);
      if (response.success) {
        this.$toasted.success(`Entity for endpoint ${item.friendlyName} successfully updated.`, {
          duration: TOASTER_DURATION,
        });
      } else {
        this.$toasted.error(response.body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }
    },
    async onDeleteClick(item, isEndpoint) {
      const name = isEndpoint ? 'Endpoint' : 'Starting Point';
      const confirm = await this.$confirm(`Deleting ${name}...`, `You are deleting ${name}. Are you sure you want to continue?`);
      if (confirm) {
        const {success, body} = isEndpoint ? await this.deleteEndpoint(item) : await this.deleteStartingPoint(item);
        if (success) {
          this.$toasted.success(`${name} deleted successfully`, {
            duration: TOASTER_DURATION,
          });
        } else {
          this.$toasted.error(body.ErrorMessage, {
            duration: TOASTER_DURATION,
          });
        }
      }
    },
    async onPreviewClick(item) {
      const params = {
        configurationId: this.$route.params.configurationId,
        endpointId: item.endpointId,
      };

      const previewCard = this.connectorCards.find(c => c.key === 'preview');
      previewCard.collapsed = false;

      const {success, body} = await this.getEndpointPreview(params);

      if (success) {
        previewCard.collapsed = false;
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: 3000,
        });
        setTimeout(() => {
          previewCard.collapsed = true;
        }, 3000);
      }
    },
    onAddClick(isEndpoint) {
      const data = {
        configurationId: this.$route.params.configurationId,
        vendor: this.connector.vendor,
      };
      if (isEndpoint) {
        this.showEndpointModal(data);
      } else {
        this.showStartingPointModal(data);
      }
    },
    isEndpointsCard(type) {
      return type === 'endpoints';
    },
    isStaticEndpointsCard(card) {
      return card.key === 'staticEndpoints';
    },
    isStartingPointsCard(type) {
      return type === 'startingPoints';
    },
    async onSaveAllClick() {
      const configurationId = this.$route.params.configurationId;

      let response;
      if (this.isPitchBook) {
        response = await this.generatePitchBook();
      } else if (this.isSQLServer) {
        response = await this.generateSQLServer();
      } else {
        response = await this.saveAllEndpointPreview(configurationId);
      }

      if (response.success) {
        this.$toasted.success('All endpoint generated successfully', {
          duration: TOASTER_DURATION,
        });
      } else {
        this.$toasted.error(response.body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }
    },
    async onSaveClick(item) {
      const params = {
        configurationId: this.$route.params.configurationId,
        endpointId: item.endpointId || item.startingPointId,
      };
      const {success, body} = await this.saveEndpointPreview(params);
      if (success) {
        this.$toasted.success('Endpoint generated successfully', {
          duration: TOASTER_DURATION,
        });
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }
    },
    async deleteStaticEndpoint(endpoint) {
      const confirm = await this.$confirm('Deleting Endpoint...', 'You are deleting Endpoint. Are you sure you want to continue?');
      if (confirm) {
        const item = this.connector.endpoints.find(e => e.endpointId === endpoint.endpointId);
        item.isStaticEndpoint = true;
        item.index = endpoint.index;
        const {success, body} = await this.deleteEndpoint(item);
        if (success) {
          this.$toasted.success('Endpoint deleted successfully', {
            duration: TOASTER_DURATION,
          });
        } else {
          this.$toasted.error(body.ErrorMessage, {
            duration: TOASTER_DURATION,
          });
        }
      } else {
        this.updateContent += 1;
      }
    },
    async onStatusChange(checked, item) {
      if (checked) {
        let response;
        if (this.isEfront) {
          const params = {
            action: 'create',
            data: {
              endpointId: uuidv4(),
              configurationId: this.$route.params.configurationId,
              url: item.name,
              friendlyName: item.originalName,
              entityName: item.originalName,
              methodType: 'GET',
            },
          };
          response = await this.saveEndpoint(params);

          item.endpointId = params.data.endpointId;

          await this.updateStaticEndpoint(item);
        } else {
          response = await this.createStaticEndpoint({
            item,
            configurationId: this.$route.params.configurationId,
            vendor: this.connector.vendor,
          });
        }

        const {success} = response;

        if (success) {
          this.$toasted.success('Endpoint created successfully', {
            duration: TOASTER_DURATION,
          });
        }
      } else {
        await this.deleteStaticEndpoint(item);
      }
    },
    resetSearchData() {
      this.staticEndpointItems = [...this.staticEndpoints];
      this.filter.keyword = '';
    },
    search(keyword) {
      if (!keyword) return this.resetSearchData();

      if (this.isDealCloud || this.isDynamo || this.isEfront || this.isSalesforce) {
        this.staticEndpointItems = this.staticEndpoints.filter(item => {
          const itemName = item.originalName || item.name;
          return itemName.toLowerCase().trim().indexOf(keyword.toLowerCase().trim()) > -1;
        });
      }
    },
    getEntityColumns(endpoint) {
      debugger;
      const endpointConfigurationJSON = endpoint.endpointConfigurationJSON;

      if (!_.isEmpty(endpointConfigurationJSON)) {
        const columnsToUpdate = JSON.parse(endpointConfigurationJSON);

        if (this.isSalesforce) {
          return columnsToUpdate ? columnsToUpdate['SalesforceColumnsToPull'] : null;
        }

        return columnsToUpdate ? columnsToUpdate['DealCloudColumnsToUpdate'] : null;
      }

      return false;
    },
    async onUpdateEndpoint(endpoint) {
      /*const entityColumns = this.getEntityColumns(endpoint);

      let columnsData;
      if (this.isDealCloud) {
        columnsData = {
          DealCloudColumnsToUpdate: entityColumns,
        }
      } else {
        columnsData = {
          SalesforceColumnsToPull: entityColumns,
        }
      }*/

      //const endpointConfigurationJSON = entityColumns.length ? JSON.stringify(columnsData) : null;

      const {success, body} = await this.saveEndpoint({
        data: {...endpoint, endpointConfigurationJSON: endpoint.endpointConfigurationJSON},
        action: 'update',
      });

      if (success) {
        this.$toasted.success(`Endpoint "${endpoint.friendlyName}" updated successfully`, {
          duration: TOASTER_DURATION,
        });
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }

      this.connector.endpoints = [...this.connector.endpoints];
      this.updateContent += 1;
    },
  },
  async mounted() {
    this.viewLoading = true;
    await this.getAllConnector();

    await this.getFields();
    if (this.isPitchBook) {
      await this.getPitchBookEndpoints();
    }

    if (this.isDealCloud || this.isDynamo || this.isSalesforce) {
      await this.getStaticEndpoints({
        configurationId: this.$route.params.configurationId,
        vendor: this.connector.vendor,
      });

      this.staticEndpointItems = [...this.staticEndpoints];

      if (this.isDealCloud || this.isSalesforce) {
        await this.getEndpointEntryColumns({
          configurationId: this.$route.params.configurationId,
          endpoints: this.connector.endpoints,
          isDealCloud: this.isDealCloud,
        });
      }
    }

    if (this.isEfront) {
      await this.getEfrontReports({
        configurationId: this.$route.params.configurationId,
        endpoints: this.connector.endpoints,
      });
      this.staticEndpointItems = [...this.staticEndpoints];
    }

    this.connector = {...this.connectors.find(c => c.configurationId === this.$route.params.configurationId)};

    // Add auth credentials in auth details form
    if (Object.keys(JSON.parse(this.connector.credentials)).length > 0) {
      const credentials = JSON.parse(this.connector.credentials);
      if (this.isHarvest) {
        this.form.accountId = credentials.AccountId;
        this.form.token = credentials.Token;
      } else if (this.isPitchBook) {
        this.form.token = credentials.Token;
      } else if (this.isDealCloud) {
        this.form.ClientId = credentials.ClientId;
        this.form.APIKey = credentials.APIKey;
      } else if (this.isSnowflake) {
        this.form.server = credentials.Server;
        this.form.region = credentials.Region;
        this.form.username = credentials.UserName;
        this.form.password = credentials.Password;
      } else if (this.isSalesforce) {
        this.form.ClientId = credentials.ClientId;
        this.form.ClientSecret = credentials.ClientSecret;
        this.form.username = credentials.UserName;
        this.form.password = credentials.Password;
        this.form.IsSandbox = credentials.IsSandbox;
        this.form.AccessKey = credentials.AccessKey;
        this.form.version = credentials.Version;
        this.form.tenant = credentials.Tenant;
      } else if (this.isBlackMountain) {
        this.form.ClientId = credentials.ClientId;
        this.form.ClientSecret = credentials.ClientSecret;
        this.form.Scope = credentials.Scope;
      } else {
        this.form.username = credentials.UserName;
        this.form.password = credentials.Password;
      }
    }

    this.$nextTick(() => {
      this.viewLoading = false;
    });
  },
  destroyed() {
    this.clearPreviewData();
    this.clearStaticEndpointsData();
  },
};
</script>

<style lang="scss">
.connector-content {
  overflow: auto;
}

.endpoint-content {
  overflow: auto;
  overflow-x: hidden;
}

.preview-content {
  overflow: auto;
}

.connector-table {
  > thead {
    > tr {
      white-space: nowrap;

      > th {
        padding: 4px 8px;
        line-height: 28px;
      }
    }
  }

  > tbody {
    > tr {
      white-space: nowrap;

      > td {
        padding: 4px 8px;
        line-height: 28px;
      }
    }
  }
}

.search-input {
  .form-group {
    margin-bottom: 0 !important;
  }
}

.static-endpoints-card .header-toggle {
  line-height: 34px;
}

.search-clear-btn {
  &:focus {
    box-shadow: none !important;
  }
}

.overflow-unset {
  overflow: unset !important;
}
</style>
